import React, { useMemo, useState } from 'react';

// Índice del árbol de una empresa: hijos por carpeta, formatos por carpeta y conteo recursivo.
export const buildTree = (categories, formats) => {
    const ids = new Set(categories.map((c) => c.id));
    const children = {};
    const formatsBy = {};
    categories.forEach((c) => {
        const parent = c.parent_id && ids.has(c.parent_id) ? c.parent_id : "root";
        (children[parent] = children[parent] || []).push(c);
    });
    formats.forEach((f) => {
        const parent = f.category_id && ids.has(f.category_id) ? f.category_id : "root";
        (formatsBy[parent] = formatsBy[parent] || []).push(f);
    });
    const counts = {};
    const count = (id) => {
        if (counts[id] !== undefined) return counts[id];
        const own = (formatsBy[id] || []).length;
        counts[id] = own + (children[id] || []).reduce((s, c) => s + count(c.id), 0);
        return counts[id];
    };
    categories.forEach((c) => count(c.id));
    return { children, formatsBy, counts };
};

const matches = (text, q) => !q || (text || "").toLowerCase().includes(q.toLowerCase());

// Árbol de selección del ORIGEN: carpetas (copian todo lo que tienen dentro) y formatos sueltos.
// Marcar una carpeta cubre a sus descendientes, que se muestran marcados y deshabilitados.
export const SelectionTree = ({ categories, formats, selectedCategories, selectedFormats, onChange }) => {
    const [open, setOpen] = useState({});
    const [q, setQ] = useState("");
    const tree = useMemo(() => buildTree(categories, formats), [categories, formats]);

    const visible = useMemo(() => {
        if (!q) return null;
        const keep = new Set();
        const walk = (id) => {
            let any = false;
            (tree.children[id] || []).forEach((c) => {
                const hit = walk(c.id) || matches(c.name, q);
                if (hit) { keep.add(`c${c.id}`); any = true; }
            });
            (tree.formatsBy[id] || []).forEach((f) => {
                if (matches(f.name, q)) { keep.add(`f${f.id}`); any = true; }
            });
            return any;
        };
        walk("root");
        return keep;
    }, [q, tree]);

    const toggleCat = (id) => {
        const next = new Set(selectedCategories);
        next.has(id) ? next.delete(id) : next.add(id);
        onChange([...next], selectedFormats);
    };
    const toggleFormat = (id) => {
        const next = new Set(selectedFormats);
        next.has(id) ? next.delete(id) : next.add(id);
        onChange(selectedCategories, [...next]);
    };

    const renderLevel = (parentId, depth, covered) => {
        const cats = tree.children[parentId] || [];
        const fmts = tree.formatsBy[parentId] || [];
        return (
            <React.Fragment>
                {cats.filter((c) => !visible || visible.has(`c${c.id}`)).map((c) => {
                    const checked = covered || selectedCategories.includes(c.id);
                    const expanded = !!visible || open[c.id];
                    const hasKids = (tree.children[c.id] || []).length + (tree.formatsBy[c.id] || []).length > 0;
                    return (
                        <React.Fragment key={`c${c.id}`}>
                            <div className={`sd-tree__row ${covered ? "is-disabled" : ""}`} style={{ paddingLeft: 12 + depth * 20 }}>
                                <button type="button" className="sd-tree__toggle" onClick={() => setOpen({ ...open, [c.id]: !open[c.id] })}>
                                    {hasKids ? <i className={`fas fa-chevron-${expanded ? "down" : "right"}`}></i> : null}
                                </button>
                                <label>
                                    <input type="checkbox" checked={checked} disabled={covered} onChange={() => toggleCat(c.id)} />
                                    <i className="fas fa-folder sd-tree__icon--folder"></i>
                                    <span className="sd-tree__name">{c.name}</span>
                                </label>
                                <span className="sd-tree__count">{tree.counts[c.id] || 0} formatos</span>
                            </div>
                            {expanded && renderLevel(c.id, depth + 1, checked)}
                        </React.Fragment>
                    );
                })}
                {fmts.filter((f) => !visible || visible.has(`f${f.id}`)).map((f) => (
                    <div key={`f${f.id}`} className={`sd-tree__row ${covered ? "is-disabled" : ""}`} style={{ paddingLeft: 30 + depth * 20 }}>
                        <label>
                            <input type="checkbox" checked={covered || selectedFormats.includes(f.id)} disabled={covered}
                                onChange={() => toggleFormat(f.id)} />
                            <i className="fas fa-file-alt sd-tree__icon--format"></i>
                            <span className="sd-tree__name">{f.name}</span>
                        </label>
                    </div>
                ))}
            </React.Fragment>
        );
    };

    return (
        <div>
            <input className="rk-input" style={{ marginBottom: 10 }} placeholder="Buscar módulo o formato…"
                value={q} onChange={(e) => setQ(e.target.value)} />
            <div className="sd-tree">
                {categories.length + formats.length === 0
                    ? <div className="sd-tree__empty">Esta empresa no tiene módulos ni formatos.</div>
                    : renderLevel("root", 0, false)}
            </div>
        </div>
    );
};

// Selector de la carpeta DESTINO: dónde queda la copia. `null` = menú principal.
export const FolderPicker = ({ categories, value, onChange }) => {
    const [open, setOpen] = useState({});
    const tree = useMemo(() => buildTree(categories, []), [categories]);

    const renderLevel = (parentId, depth) => (tree.children[parentId] || []).map((c) => {
        const hasKids = (tree.children[c.id] || []).length > 0;
        return (
            <React.Fragment key={c.id}>
                <div className={`sd-radio-row ${value === c.id ? "is-active" : ""}`} style={{ paddingLeft: 12 + depth * 20 }}
                    onClick={() => onChange(c.id)}>
                    <button type="button" className="sd-tree__toggle"
                        onClick={(e) => { e.stopPropagation(); setOpen({ ...open, [c.id]: !open[c.id] }); }}>
                        {hasKids ? <i className={`fas fa-chevron-${open[c.id] ? "down" : "right"}`}></i> : null}
                    </button>
                    <i className="fas fa-folder sd-tree__icon--folder"></i> {c.name}
                </div>
                {open[c.id] && renderLevel(c.id, depth + 1)}
            </React.Fragment>
        );
    });

    return (
        <div className="sd-tree">
            <div className={`sd-radio-row ${value === null ? "is-active" : ""}`} onClick={() => onChange(null)}>
                <span className="sd-tree__toggle"></span>
                <i className="fas fa-home" style={{ color: "#0061fe" }}></i> <strong>Menú principal</strong>
                <span className="sd-muted">&nbsp;(la copia queda como módulo de primer nivel)</span>
            </div>
            {renderLevel("root", 0)}
        </div>
    );
};
